import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Input from "../../../components/common/Input";
import useAuth from "../../../hooks/useAuth";
import { signupAPI } from "../../../api/index";
import "./SignupMobilePage.css";

const SignupMobilePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    phone: "",
    email: "",
    name: "",
    companyName: "",
    businessFile: null,
  });

  const { getUserInfo } = useAuth();
  const [errors, setErrors] = useState({});
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  const [checkedUsername, setCheckedUsername] = useState(""); // 중복확인한 아이디 저장
  const [selectedFileName, setSelectedFileName] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        businessFile: file,
      }));
      setSelectedFileName(file.name);
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      confirmPassword: "",
      phone: "",
      email: "",
      name: "",
      companyName: "",
      businessFile: null,
    });
    setErrors({});
    setIsUsernameAvailable(null);
    setCheckedUsername("");
    setSelectedFileName("");
  };

  const handleClose = () => {
    resetForm();
    navigate("/login");
  };

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/[^0-9]/g, "");
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(
        7,
        11
      )}`;
    }
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;
    if (field === "phone") {
      processedValue = formatPhoneNumber(value);
    } else {
      processedValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));

    if (field === "username") {
      // 중복확인한 아이디와 현재 입력된 아이디가 다르면 초기화
      const trimmedValue = value.trim();
      if (checkedUsername && trimmedValue !== checkedUsername) {
        setIsUsernameAvailable(null);
        setCheckedUsername("");
      }
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleBlur = (field) => {
    if (field === "phone") {
      return;
    }
    const trimmedValue = formData[field].trim();
    setFormData((prev) => ({
      ...prev,
      [field]: trimmedValue,
    }));
  };

  const checkUsernameAvailability = async () => {
    const trimmedUsername = formData.username.trim();
    if (!trimmedUsername) {
      alert("아이디를 먼저 입력해주세요.");
      return;
    }
    if (trimmedUsername.length < 8) {
      alert("아이디는 8자 이상이어야 합니다.");
      return;
    }
    if (!/^[a-zA-Z0-9]+$/.test(trimmedUsername)) {
      alert("아이디는 영문과 숫자만 사용 가능합니다.");
      return;
    }

    try {
      const result = await getUserInfo(trimmedUsername);
      if (result.success && result.isAvailable) {
        setIsUsernameAvailable(true);
        setCheckedUsername(trimmedUsername); // 중복확인한 아이디 저장
        alert("사용 가능한 아이디입니다.");
      } else {
        setIsUsernameAvailable(false);
        setCheckedUsername(""); // 중복확인 실패 시 초기화
        alert(result.message || "중복 확인 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error("중복 확인 오류:", error);
      setIsUsernameAvailable(false);
      setCheckedUsername(""); // 오류 시 초기화
      alert("중복 확인 중 오류가 발생했습니다.");
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const trimmedData = {
      username: formData.username.trim(),
      password: formData.password.trim(),
      confirmPassword: formData.confirmPassword.trim(),
      phone: formData.phone.replace(/[^0-9]/g, ""),
      email: formData.email.trim(),
      name: formData.name.trim(),
      companyName: formData.companyName.trim(),
    };

    if (!trimmedData.username) {
      newErrors.username = "아이디를 입력해주세요";
    } else if (trimmedData.username.length < 8) {
      newErrors.username = "아이디는 8자 이상이어야 합니다";
    } else if (!/^[a-zA-Z0-9]+$/.test(trimmedData.username)) {
      newErrors.username = "아이디는 영문과 숫자만 사용 가능합니다";
    } else if (isUsernameAvailable === null) {
      newErrors.username = "아이디 중복 확인을 해주세요";
    } else if (isUsernameAvailable === false) {
      newErrors.username = "중복된 아이디입니다. 다른 아이디를 사용해주세요";
    }

    if (!trimmedData.password) {
      newErrors.password = "비밀번호를 입력해주세요";
    } else if (trimmedData.password.length < 6) {
      newErrors.password = "비밀번호는 6자 이상이어야 합니다";
    }

    if (trimmedData.password !== trimmedData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호를 다시 입력해주세요";
    }
    if (!trimmedData.name) {
      newErrors.name = "이름을 입력해주세요";
    }

    if (!trimmedData.phone) {
      newErrors.phone = "휴대폰번호를 입력해주세요";
    } else if (trimmedData.phone.length < 10) {
      newErrors.phone = "올바른 휴대폰번호를 입력해주세요";
    } else if (!/^01[0-9]/.test(trimmedData.phone)) {
      newErrors.phone = "010, 011, 012, 019로 시작하는 번호를 입력해주세요";
    }

    if (!trimmedData.email) {
      newErrors.email = "이메일을 입력해주세요";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedData.email)) {
      newErrors.email = "올바른 이메일 형식을 입력해주세요";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const apiData = {
        username: formData.username.trim(),
        password: formData.password.trim(),
        name: formData.name.trim(),
        phone: formData.phone.replace(/[^0-9]/g, ""),
        email: formData.email.trim(),
        company: formData.companyName.trim() || undefined,
        business_license_url: formData.businessFile ? "https://..." : undefined,
      };

      try {
        const result = await signupAPI(apiData);
        if (result === "") {
          alert("회원가입이 완료되었습니다!");
          handleClose();
        } else {
          alert(`회원가입 실패: ${result}`);
        }
      } catch (error) {
        console.error("회원가입 오류:", error);
        alert("회원가입 중 오류가 발생했습니다.");
      }
    }
  };

  return (
    <div className="signup-mobile-page-root">
      {/* Header */}
      <div className="signup-mobile-header">
        <button className="signup-mobile-back-btn" onClick={handleClose}>
          <svg
            className="signup-mobile-back-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="#B4B4B4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="signup-mobile-header-title">회원가입</div>
        <div className="signup-mobile-header-spacer"></div>
      </div>

      {/* Content */}
      <div className="signup-mobile-content">
        <form className="signup-mobile-form" onSubmit={handleSubmit}>
          {/* 아이디 */}
          <div className="signup-mobile-field">
            <div className="signup-mobile-label">아이디</div>
            <div className="signup-mobile-input-row">
              <div
                className={`signup-mobile-input-wrapper ${
                  formData.username && formData.username.trim()
                    ? "has-value"
                    : ""
                }`}
              >
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) =>
                    handleInputChange("username", e.target.value)
                  }
                  onBlur={() => handleBlur("username")}
                  placeholder="Enter"
                  className="signup-mobile-input"
                />
              </div>
              <button
                type="button"
                className="signup-mobile-check-btn"
                onClick={checkUsernameAvailability}
              >
                중복확인
              </button>
            </div>
            {errors.username && (
              <div className="signup-mobile-error">{errors.username}</div>
            )}
            {isUsernameAvailable === true && (
              <div className="signup-mobile-success">
                사용 가능한 아이디입니다.
              </div>
            )}
            {isUsernameAvailable === false && (
              <div className="signup-mobile-error">중복된 아이디입니다.</div>
            )}
          </div>

          {/* 비밀번호 */}
          <div className="signup-mobile-field">
            <div className="signup-mobile-label">비밀번호</div>
            <div
              className={`signup-mobile-input-wrapper ${
                formData.password && formData.password.trim()
                  ? "has-value"
                  : ""
              }`}
            >
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                onBlur={() => handleBlur("password")}
                placeholder="Enter"
                className="signup-mobile-input"
              />
            </div>
            {errors.password && (
              <div className="signup-mobile-error">{errors.password}</div>
            )}
          </div>

          {/* 비밀번호 확인 */}
          <div className="signup-mobile-field">
            <div className="signup-mobile-label">비밀번호 확인</div>
            <div
              className={`signup-mobile-input-wrapper ${
                formData.confirmPassword && formData.confirmPassword.trim()
                  ? "has-value"
                  : ""
              }`}
            >
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleInputChange("confirmPassword", e.target.value)
                }
                onBlur={() => handleBlur("confirmPassword")}
                placeholder="Enter"
                className="signup-mobile-input"
              />
            </div>
            {errors.confirmPassword && (
              <div className="signup-mobile-error">
                {errors.confirmPassword}
              </div>
            )}
          </div>

          {/* 휴대폰번호 */}
          <div className="signup-mobile-field">
            <div className="signup-mobile-label">휴대폰번호</div>
            <div
              className={`signup-mobile-input-wrapper ${
                formData.phone && formData.phone.trim() ? "has-value" : ""
              }`}
            >
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                onBlur={() => handleBlur("phone")}
                placeholder="010-1234-5678"
                className="signup-mobile-input"
                maxLength={13}
              />
            </div>
            {errors.phone && (
              <div className="signup-mobile-error">{errors.phone}</div>
            )}
          </div>

          {/* E-mail */}
          <div className="signup-mobile-field">
            <div className="signup-mobile-label">E-mail</div>
            <div
              className={`signup-mobile-input-wrapper ${
                formData.email && formData.email.trim() ? "has-value" : ""
              }`}
            >
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                placeholder="qwer1234@naver.com"
                className="signup-mobile-input"
              />
            </div>
            {errors.email && (
              <div className="signup-mobile-error">{errors.email}</div>
            )}
          </div>

          {/* 이름 */}
          <div className="signup-mobile-field">
            <div className="signup-mobile-label">이름</div>
            <div
              className={`signup-mobile-input-wrapper ${
                formData.name && formData.name.trim() ? "has-value" : ""
              }`}
            >
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                onBlur={() => handleBlur("name")}
                placeholder="홍길동"
                className="signup-mobile-input"
              />
            </div>
            {errors.name && (
              <div className="signup-mobile-error">{errors.name}</div>
            )}
          </div>

          {/* 업체명(선택) */}
          <div className="signup-mobile-field">
            <div className="signup-mobile-label">업체명(선택)</div>
            <div
              className={`signup-mobile-input-wrapper ${
                formData.companyName && formData.companyName.trim()
                  ? "has-value"
                  : ""
              }`}
            >
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) =>
                  handleInputChange("companyName", e.target.value)
                }
                onBlur={() => handleBlur("companyName")}
                placeholder="(주)컴퍼니"
                className="signup-mobile-input"
              />
            </div>
            {errors.companyName && (
              <div className="signup-mobile-error">{errors.companyName}</div>
            )}
          </div>

          {/* 회원가입 버튼 */}
          <button 
            type="submit" 
            className="signup-mobile-submit-btn"
            disabled={isUsernameAvailable !== true}
          >
            회원가입
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignupMobilePage;

